import Add from '@mui/icons-material/Add';
import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline';
import Star from '@mui/icons-material/Star';
import StarBorder from '@mui/icons-material/StarBorder';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  IconButton,
  Snackbar,
  Stack,
  Typography,
  useMediaQuery,
} from '@mui/material';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useGraphQLMutation } from '../../customHooks/useGraphQL';
import {
  IAISolution,
  IAllAgents,
  IEnhancedAISolution,
  IMutationAISolution,
} from '../../common/types/common';
import Loader from '../../common/components/Loader';
import Modal from '../../common/components/Modal/Modal';
import ConfirmationModal from '../../common/components/ConfirmationModal';
import { getBenefitLabelNew, getBenefitTitle } from '../../common/utils';
import { API_BASE } from '../../config/env';
import { httpConstants } from '../../constants';
import { UPDATE_FAVORITE_AI_SOLUTIONS } from '../../graphql/mutations/common';
import { httpService } from '../../utility/httpService';
import SolutionEditModal from './SolutionEditModal';
import TopBenefits from './TopBenefits';
import ImplementationComplexity from './ImplementationComplexity';
import { RecommendedBadge } from '../../common/components/RecommendBadge/RecommendedBadge';
import { RootState } from '../../store';

import {
  EditType,
  NormalizedAgentData,
  ViewSolutionDetailsProps,
} from './ViewSolutionDetails/types';
import { useSolutionFetch } from './ViewSolutionDetails/hooks/useSolutionFetch';
import { useManualSync } from './ViewSolutionDetails/hooks/useManualSync';
import { useVaultState } from './ViewSolutionDetails/hooks/useVaultState';
import { useEnterpriseTechSync } from './ViewSolutionDetails/hooks/useEnterpriseTechSync';
import {
  getApprovalStatusConfig,
  getSvgPathForComplexity,
  getSvgPathForRoi,
  handleEnablerText,
  normalizeAgentData,
} from './ViewSolutionDetails/solutionUtils';
import { updateCachedSolutionData } from './ViewSolutionDetails/solutionCache';
import { fetchAISolutionWithAgentEnablement } from '../../utility/aiOpportunityUtils';
import SolutionHeader from './ViewSolutionDetails/components/SolutionHeader';
import DataSourcesPanel from './ViewSolutionDetails/components/DataSourcePanel';
import KeyRequirementsPanel from './ViewSolutionDetails/components/KeyRequirementsPanel';
import AiEnablementView from './ViewSolutionDetails/components/AiEnablementView';
import AgentsView from './ViewSolutionDetails/components/AgentsView';

const DEFAULT_AGENT_DATA: NormalizedAgentData = { embedded: [], extended: [], custom: [] };

const ViewSolutionDetails: React.FC<ViewSolutionDetailsProps> = ({
  selectedSolutionId,
  selectedSolutionFromLibrary,
  enterpriseContextApplied = false,
  setEnterpriseContextApplied = () => {},
  aiSolutionData,
  setAiSolutionData,
  diagramId,
  handleToggleVault,
  handleUpdateSolution,
  handleSave,
  isEditable = true,
  isProcessDiagramActive = false,
  onApproval,
  handleRemoveClick,
  closeDrawer,
  isIdeation = false,
  processType = 'standard',
  fromList = false,
  isProcessAiOpportunities = false,
  breadcrumb = [],
  processName = '',
  isVersionViewMode = false,
  onRestore,
}) => {
  // ── Shared derived state ────────────────────────────────────────────────────
  const currentOrg = useMemo(() => localStorage.getItem('current_organization') || 'Empty', []);
  const isMobile = useMediaQuery('(max-width: 600px)');
  const navigate = useNavigate();

  const selectedSubprocess = useSelector(
    (state: RootState) => state.taxonomyXPLR.selectedSubprocess
  );
  const customUseCases = useSelector((state: RootState) => state.allTaxonomies.customUseCases);

  // ── Local UI state ──────────────────────────────────────────────────────────
  const [, setSolution] = useState<IEnhancedAISolution | null>(null);
  const [isFavorite, setIsFavorite] = useState(false);
  const [showApplySuccessSnackbar, setShowApplySuccessSnackbar] = useState(false);
  const [activePiece, setActivePiece] = useState<string>('information');
  const [agentLlmResponse, setAgentLlmResponse] = useState<any[]>([]);
  const [isLoadingAgentLlmResponse, setIsLoadingAgentLlmResponse] = useState(false);
  const [showFavoriteModal, setShowFavoriteModal] = useState(false);
  const [llmResponseLoading, setLlmResponseLoading] = useState(false);
  const [llmResponseData, setLlmResponseData] = useState<any[]>([]);
  const [isLoadingImplementation] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [updatedSolutionData, setUpdatedSolutionData] = useState<any>(null);
  const [editItem, setEditItem] = useState<any>(null);
  const [originalSolutionData, setOriginalSolutionData] = useState<any>(null);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [editType, setEditType] = useState<EditType | string>('');
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [updatedAiSolution, setUpdatedAiSolution] = useState<any>(null);
  const [hasEnterpriseTechApplied, setHasEnterpriseTechApplied] = useState(false);
  const [approvalStatus, setApprovalStatus] = useState<string | null>(null);
  const [, setIsLoadingEnterpriseTech] = useState(false);
  const [normalizedAgentData, setNormalizedAgentData] =
    useState<NormalizedAgentData>(DEFAULT_AGENT_DATA);

  // ── Refs ────────────────────────────────────────────────────────────────────
  const currentSolutionIdRef = useRef<any>(null);
  const hasLoadedImplementationRef = useRef(false);
  const lastValidDisplayDataRef = useRef<any>(null);
  const isSavingRef = useRef(false);
  const hasFetchedEnterpriseTechRef = useRef(false);
  const missingAgentDataDecisionCountRef = useRef<Record<string, number>>({});
  const previousNormalizedDataRef = useRef<NormalizedAgentData>(DEFAULT_AGENT_DATA);

  // ── Hooks ───────────────────────────────────────────────────────────────────
  const { queryLoading, queryError, fetchSolutionById } = useSolutionFetch({
    selectedSolutionId,
    selectedSolutionFromLibrary,
    diagramId,
    currentOrg,
    isIdeation,
    processType,
    isVersionViewMode,
    hasFetchedEnterpriseTechRef,
    setAiSolutionData,
    setAgentLlmResponse,
    setLlmResponseData,
    setIsLoadingAgentLlmResponse,
    setLlmResponseLoading,
    setUpdatedSolutionData,
    setHasEnterpriseTechApplied,
  });

  const {
    isManualSyncLoading,
    handleAgentTabClick: handleManualSyncTabClick,
    resetAdminSyncState,
  } = useManualSync({
    aiSolutionData,
    selectedSolutionId,
    currentOrg,
    diagramId,
    processType,
    setAgentLlmResponse,
    setLlmResponseData,
    setUpdatedSolutionData,
    setAiSolutionData,
    setLlmResponseLoading,
  });

  const { localVaultState, isVaultActionLoading, handleVaultClick } = useVaultState({
    aiSolutionData,
    selectedSolutionFromLibrary,
    updatedAiSolution,
    selectedSolutionId,
    handleToggleVault,
  });

  useEnterpriseTechSync({
    aiSolutionData,
    selectedSolutionId,
    currentOrg,
    diagramId,
    processType,
    hasFetchedEnterpriseTechRef,
    missingAgentDataDecisionCountRef,
    setAgentLlmResponse,
    setLlmResponseData,
    setUpdatedSolutionData,
    setAiSolutionData,
    setHasEnterpriseTechApplied,
    setLlmResponseLoading,
    setIsLoadingAgentLlmResponse,
    setIsLoadingEnterpriseTech,
  });

  const { mutate } = useGraphQLMutation<IMutationAISolution>(UPDATE_FAVORITE_AI_SOLUTIONS, {
    onCompleted: (data) => console.log('Favorite updated:', data),
    onError: (error) => console.error('Error updating favorite:', error),
    refetchQueries: ['IMutationAISolution'],
  });

  // ── Derived display data ────────────────────────────────────────────────────
  const displayData = updatedSolutionData || aiSolutionData || lastValidDisplayDataRef.current;

  useEffect(() => {
    if (updatedSolutionData || aiSolutionData)
      lastValidDisplayDataRef.current = updatedSolutionData || aiSolutionData;
  }, [updatedSolutionData, aiSolutionData]);

  const isIdeationEnablementLoading = isManualSyncLoading || isLoadingAgentLlmResponse;

  const effectiveIsIdeation = useMemo(() => {
    if (fromList) return displayData?.subprocess_type === 'custom';
    if (processType === 'custom') return true;
    if (displayData?.ideation_xplr === true) return true;
    if (displayData?.subprocess_type === 'custom') return true;
    return false;
  }, [displayData, isIdeation, processType, fromList]);

  const showAiAutomation = effectiveIsIdeation || processType === 'custom';

  const customUseCaseMap = useMemo(() => {
    const map: Record<string, string> = {};
    customUseCases?.forEach((uc: any) => {
      if (uc.level2_id) map[uc.level2_id] = uc.sequential_id;
    });
    return map;
  }, [customUseCases]);

  const subprocessIdForMapping = displayData?.subprocess_id || selectedSubprocess?.id;
  const sequentialId =
    customUseCaseMap[subprocessIdForMapping] ||
    (selectedSubprocess?.subprocesses as any[])?.find(
      (sub: any) => String(sub.id) === String(displayData?.subprocess_id)
    )?.sequentialId ||
    selectedSubprocess?.sequentialId;

  const recommended = displayData?.implementation_complexity?.recommendation;

  // ── Solution change reset ───────────────────────────────────────────────────
  useEffect(() => {
    if (currentSolutionIdRef.current !== selectedSolutionId) {
      currentSolutionIdRef.current = selectedSolutionId;
      hasLoadedImplementationRef.current = false;
      hasFetchedEnterpriseTechRef.current = false;
      resetAdminSyncState();
      setActivePiece('information');
      setUpdatedSolutionData(null);
      setOriginalSolutionData(null);
      setIsEditing(false);
    }
  }, [selectedSolutionId, resetAdminSyncState]);

  // When agent metadata arrives after initial fetch, allow sync to re-evaluate
  useEffect(() => {
    if (!selectedSolutionId || !aiSolutionData?.id) return;
    if (String(aiSolutionData.id) !== String(selectedSolutionId)) return;
    hasFetchedEnterpriseTechRef.current = false;
  }, [
    selectedSolutionId,
    aiSolutionData?.id,
    aiSolutionData?.agent?.updated_at,
    isVersionViewMode,
  ]);

  useEffect(() => {
    if (selectedSolutionId) fetchSolutionById();
  }, [selectedSolutionId, fetchSolutionById]);

  useEffect(() => {
    if (!queryLoading && !isEditing && aiSolutionData && selectedSolutionId) {
      if (String(aiSolutionData.id) === String(selectedSolutionId))
        setOriginalSolutionData(JSON.parse(JSON.stringify(aiSolutionData)));
    }
  }, [selectedSolutionId, queryLoading, isEditing]);

  // Sync aiSolutionData into display state
  useEffect(() => {
    if (queryLoading || queryError || !aiSolutionData) return;

    const enhancedSolution: IEnhancedAISolution = processSolutionData(aiSolutionData);
    setSolution(enhancedSolution);
    setIsFavorite(aiSolutionData.favorite || false);

    if (aiSolutionData.agent) {
      const agentData = aiSolutionData.agent;
      if (
        agentData.ai_enablement ||
        agentData.agent_classification ||
        agentData.embedded ||
        agentData.extended ||
        agentData.custom
      ) {
        setAgentLlmResponse(
          agentData.agent_classification ||
            agentData.embedded ||
            agentData.extended ||
            agentData.custom ||
            agentData.ai_enablement ||
            []
        );
        setLlmResponseData(agentData.ai_solution || agentData.application_in_use || []);
      }
    }
    setApprovalStatus(aiSolutionData.status || null);
    if (!isSavingRef.current) setUpdatedSolutionData(aiSolutionData);
  }, [aiSolutionData, updatedAiSolution, queryLoading, queryError]);

  // Track updatedAiSolution for vault state
  useEffect(() => {
    if (aiSolutionData && Object.keys(aiSolutionData).includes('agent'))
      setUpdatedAiSolution(aiSolutionData);
  }, [aiSolutionData]);

  // Enterprise context reset
  useEffect(() => {
    if (enterpriseContextApplied) setEnterpriseContextApplied(false);
  }, [enterpriseContextApplied, setEnterpriseContextApplied]);

  // Cleanup on unmount
  useEffect(() => () => setAiSolutionData?.(null), [setAiSolutionData]);
  useEffect(() => () => setActivePiece('information'), []);

  // Normalized agent data update
  useEffect(() => {
    if (!displayData) {
      setNormalizedAgentData(DEFAULT_AGENT_DATA);
      return;
    }

    const isIntermediate =
      isIdeationEnablementLoading &&
      displayData?.agent?.ai_solution &&
      !displayData?.agent?.embedded?.length &&
      !displayData?.agent?.extended?.length &&
      !displayData?.agent?.custom?.length &&
      !displayData?.agent?.ai_enablement?.enablement_matrix;

    if (isIntermediate) {
      setNormalizedAgentData(previousNormalizedDataRef.current);
      return;
    }

    const normalized = normalizeAgentData(displayData, effectiveIsIdeation);
    const hasContent =
      normalized.embedded.length > 0 ||
      normalized.extended.length > 0 ||
      normalized.custom.length > 0;
    if (hasContent) previousNormalizedDataRef.current = normalized;
    setNormalizedAgentData(normalized);
  }, [displayData, isIdeationEnablementLoading, effectiveIsIdeation]);

  // ── Edit handlers ───────────────────────────────────────────────────────────
  const handleEdit = (type: EditType, item?: any) => {
    setEditItem(item || null);
    setOpenEditModal(true);
    setEditType(type);
  };

  const handleDiscard = () => {
    setUpdatedSolutionData(
      originalSolutionData ? JSON.parse(JSON.stringify(originalSolutionData)) : aiSolutionData
    );
    setOpenEditModal(false);
    setIsEditing(false);
    setEditItem(null);
    setEditType('');
  };

  const handleSaveSolution = (data: any) => {
    setUpdatedSolutionData(data);
    setAiSolutionData?.(data);
    updateCachedSolutionData(data);
    handleUpdateSolution(data);
  };

  const handleSaveClick = async () => {
    isSavingRef.current = true;
    try {
      handleSave(updatedSolutionData);
      updateCachedSolutionData(updatedSolutionData);
      if (aiSolutionData.status === 'approved') {
        const url = `${API_BASE}/api/v1/e2e_taxonomy/new_ai_opportunity/${selectedSolutionId}/`;
        await httpService(httpConstants.METHOD_TYPE.PATCH, url, {
          status: 'approval_pending',
          isAddOpportunityStatusHistory: true,
          organization_id: currentOrg,
        });
      }
      setApprovalStatus('approval_pending');
      setIsEditing(false);
    } finally {
      setTimeout(() => {
        isSavingRef.current = false;
      }, 3000);
    }
  };

  const handleSendForApproval = async () => {
    try {
      const url = `${API_BASE}/api/v1/e2e_taxonomy/new_ai_opportunity/${selectedSolutionId}/`;
      await httpService(httpConstants.METHOD_TYPE.PATCH, url, {
        status: 'approval_pending',
        isAddOpportunityStatusHistory: true,
        organization_id: currentOrg,
      });
      setApprovalStatus('pending');
      const updated = await fetchAISolutionWithAgentEnablement({
        solutionId: String(selectedSolutionId),
        diagramId: diagramId || '',
        organizationId: currentOrg,
      });
      if (onApproval) {
        try {
          onApproval(updated);
        } catch (e) {
          console.warn('onApproval callback failed', e);
        }
      }
    } catch (error) {
      console.error('Error sending for approval:', error);
    }
  };

  const handleToggleFavorite = useCallback(() => {
    const newState = !isFavorite;
    setIsFavorite(newState);
    mutate({ variables: { v1: { id: selectedSolutionId, favorite: newState } } });
    setShowFavoriteModal(false);
  }, [isFavorite, mutate, selectedSolutionId]);

  // Wraps handleAgentTabClick from useManualSync to also update activePiece
  const handleTabClick = useCallback(
    (tabKey: string) => {
      handleManualSyncTabClick(tabKey);
      setActivePiece(tabKey);
    },
    [handleManualSyncTabClick]
  );

  // ── Helper: process solution data ───────────────────────────────────────────
  const processSolutionData = useCallback((rawSolution: IAISolution): IEnhancedAISolution => {
    const allDataRequirements =
      rawSolution.aiOpportunitiesUsed?.flatMap((item) => item.data_requirements || []).join(', ') ||
      '';
    const allTopBenefits = rawSolution?.benefits
      ? Object.entries(rawSolution.benefits).map(([key, value]) => ({
          title: getBenefitTitle(key),
          label: getBenefitLabelNew(value as string),
        }))
      : [];
    const keyConsiderations = rawSolution?.keyConsiderations
      ? rawSolution.keyConsiderations
      : rawSolution.potentialChallenges
          ?.split(',')
          .map((i) => i.trim())
          .filter((i) => i.length > 0) || [];
    const allAgents: IAllAgents | undefined = rawSolution.agentCollaboration?.length
      ? {
          description: rawSolution.agentCollaboration.map((c) => c.collaboration).join(' '),
          agents: rawSolution.agentCollaboration
            .flatMap((c) => c.agents)
            .map((agent) => {
              const match = rawSolution.aiOpportunitiesUsed?.find(
                (o) => o.ai_opportunity_id === agent.agent_id
              );
              const isMandatory = match?.must_have === 'MUST HAVE';
              return {
                label: agent.agent_name,
                mustHave: match?.must_have,
                mustHaveLabel: isMandatory ? 'Essential' : 'Optional',
                className: isMandatory ? 'yellow' : 'blue',
              };
            }),
        }
      : undefined;
    return {
      ...rawSolution,
      allDataRequirements,
      allTopBenefits,
      keyConsiderations,
      allAgents,
      impact: rawSolution.impact,
    };
  }, []);

  // ── Loading / error / empty guards ──────────────────────────────────────────
  if (queryLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '80vh',
          width: '100%',
        }}
      >
        <Loader message="Fetching AI Opportunity" />
      </Box>
    );
  }

  if (queryError) {
    return (
      <Box
        sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}
      >
        <Typography color="error">Error loading solution data</Typography>
      </Box>
    );
  }

  if (!displayData || !selectedSolutionId) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '80vh',
          width: '100%',
        }}
      >
        <Typography>No solution data available. Please select a solution.</Typography>
      </Box>
    );
  }

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <>
      {/* Breadcrumb header */}
      {processName && breadcrumb.length > 0 && !isMobile && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            p: 2,
            borderBottom: !isProcessAiOpportunities ? '1px solid #E5E7EB' : 'none',
            backgroundColor: '#FFFFFF',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
            {breadcrumb.map((item, index) => (
              <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography
                  sx={{ fontSize: { xs: '12px', sm: '14px' }, color: '#737373', fontWeight: 400 }}
                >
                  {index === breadcrumb.length - 1
                    ? `${displayData?.process_name || item} / ${sequentialId || ''}`
                    : item}
                </Typography>
                {index < breadcrumb.length - 1 && (
                  <Typography sx={{ fontSize: '14px', color: '#737373' }}>/</Typography>
                )}
              </Box>
            ))}
          </Box>
          <Typography
            sx={{ fontSize: { xs: '20px', sm: '26px' }, fontWeight: 600, color: '#313131' }}
          >
            {displayData?.subprocess_name}
          </Typography>
        </Box>
      )}

      {/* Title header bar */}
      <SolutionHeader
        displayData={displayData}
        effectiveIsIdeation={effectiveIsIdeation}
        isEditable={isEditable}
        isProcessDiagramActive={isProcessDiagramActive}
        isEditing={isEditing}
        approvalStatus={approvalStatus}
        localVaultState={localVaultState}
        isVaultActionLoading={isVaultActionLoading}
        isProcessAiOpportunities={isProcessAiOpportunities}
        closeDrawer={closeDrawer}
        onEdit={handleEdit}
        onSave={handleSaveClick}
        onDiscard={handleDiscard}
        onDelete={() => setShowDeleteConfirmModal(true)}
        onSendForApproval={handleSendForApproval}
        onVaultClick={handleVaultClick}
        onToggleEditing={setIsEditing}
        recommended={recommended}
        isVersionViewMode={isVersionViewMode}
        onRestore={onRestore}
      />

      <Box
        sx={{
          p: isProcessAiOpportunities ? { xs: 0, sm: 2 } : 0,
          paddingTop: 0,
          mb: isProcessAiOpportunities ? 2 : 0,
        }}
      >
        <Box
          sx={{
            border: isProcessAiOpportunities ? '1px solid #E5E7EB' : 'none',
            borderRadius: isProcessAiOpportunities ? '10px' : 'none',
            p: isProcessAiOpportunities ? { xs: 0.5, sm: 1 } : 0,
            px: { xs: 0.5, sm: 2 },
            mb: isProcessAiOpportunities ? 1 : 0,
          }}
        >
          {/* Solution card header (name, impact badge, vault) */}
          <Box
            sx={{
              mx: { xs: 0, sm: 2 },
              p: { xs: 1, sm: 2 },
              pt: { xs: recommended ? 4 : 1, sm: recommended ? 5 : 2 },
              position: 'relative',
              display: 'flex',
              alignItems: { xs: 'stretch', sm: 'center' },
              justifyContent: 'space-between',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: { xs: 1.5, sm: 0 },
              border: !isProcessAiOpportunities
                ? recommended
                  ? '1px solid #1A55F9'
                  : '1px solid #e0e0e0'
                : 'none',
              borderRadius: 1,
            }}
          >
            {recommended && (
              <Box sx={{ position: 'absolute', top: 0, left: 0 }}>
                <RecommendedBadge />
              </Box>
            )}

            {/* Left: name + mobile-specific metadata */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                flex: 1,
                minWidth: 0,
              }}
            >
              {/* Mobile: show process name above opportunity name */}
              {isMobile && displayData?.process_name && (
                <Typography sx={{ fontSize: '8px', color: '#6B7280', mb: 1, lineHeight: 1.4 }}>
                  {displayData.process_name}
                </Typography>
              )}
              {/* Mobile: show solution enabler in blue above opportunity name */}
              {isMobile && displayData?.solution_enabler && (
                <Typography
                  sx={{
                    fontSize: '12px',
                    color: '#1A55F9',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.3,
                  }}
                >
                  {handleEnablerText(displayData?.solution_enabler)}
                </Typography>
              )}
              {/* Desktop: sequential_id • solution_enabler inline */}
              {!isMobile && (
                <Typography variant="caption" sx={{ fontWeight: 600 }}>
                  <span style={{ color: '#737373' }}>{displayData?.sequential_id} •</span>{' '}
                  <span style={{ color: '#1A55F9' }}>
                    {handleEnablerText(displayData?.solution_enabler)}
                  </span>
                </Typography>
              )}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontSize: { xs: '18px', sm: '24px' },
                    fontWeight: 700,
                    lineHeight: 1.1,
                    wordBreak: 'break-word',
                    flex: 1,
                    minWidth: 0,
                  }}
                >
                  {displayData?.opportunity_name}
                </Typography>
                {isEditing && (
                  <DriveFileRenameOutlineIcon
                    sx={{ cursor: 'pointer', height: '16px', width: '16px', color: 'grey.500' }}
                    onClick={() => handleEdit('name')}
                  />
                )}
                {/* Mobile: vault icon button inline with title */}
                {isMobile && !isProcessDiagramActive && (
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: 2,
                      border: '1px solid #1A55F9',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: '#F8FAFF',
                    }}
                  >
                    <IconButton
                      onClick={() => handleToggleVault()}
                      sx={{ p: 0, width: 28, height: 28 }}
                    >
                      <img src="/images/Vault.png" alt="vault" style={{ width: 18, height: 18 }} />
                    </IconButton>
                  </Box>
                )}
              </Box>
            </Box>

            {/* Right: impact badge + vault (desktop only) */}
            <Box
              sx={{
                display: { xs: 'none', sm: 'flex', md: 'flex' },
                alignItems: 'center',
                gap: 2,
                ml: 2,
              }}
            >
              <Box
                sx={{
                  background: '#ffffff',
                  borderRadius: '39px',
                  border: '1.5px solid #1C53F5',
                  display: 'inline-flex',
                  alignItems: 'center',
                  px: 2,
                  height: '28px',
                  boxShadow: 1,
                }}
              >
                <Typography
                  sx={{
                    color: '#1B3FD1',
                    fontFamily: 'Poppins',
                    fontWeight: 500,
                    fontSize: '14px',
                    textTransform: 'uppercase',
                  }}
                >
                  {String(displayData?.impact)}
                </Typography>
              </Box>
              {isEditing && (
                <DriveFileRenameOutlineIcon
                  sx={{ cursor: 'pointer', fontSize: 18, color: 'grey.500', mt: '2px' }}
                  onClick={() => handleEdit('impact')}
                />
              )}

              {/* Status chip — shown next to impact badge when isIdeation and status is valid */}
              {isIdeation &&
                (() => {
                  const statusConfig = getApprovalStatusConfig(approvalStatus);
                  if (!statusConfig.show) return null;
                  return (
                    <Chip
                      label={statusConfig.text}
                      sx={{
                        height: '28px',
                        borderRadius: '4px',
                        fontFamily: 'Poppins',
                        fontWeight: 500,
                        fontSize: '12px',
                        color: statusConfig.color,
                        backgroundColor: statusConfig.bgColor,
                        border: 'none',
                        '& .MuiChip-label': { padding: '1px 6px' },
                      }}
                    />
                  );
                })()}

              {isProcessAiOpportunities && (
                <Button
                  variant="outlined"
                  startIcon={<img src="/images/Vault.png" alt="vault" />}
                  onClick={handleVaultClick}
                  disabled={isVaultActionLoading}
                  sx={{
                    textTransform: 'none',
                    fontSize: '12px',
                    fontWeight: 500,
                    whiteSpace: 'nowrap',
                    px: 1.5,
                  }}
                >
                  {`${localVaultState ? 'Remove from' : 'Add to'} my list`}
                  {isVaultActionLoading && <span style={{ marginLeft: 8, opacity: 0.7 }}>…</span>}
                </Button>
              )}
            </Box>

            {/* Mobile: impact badge shown below name */}
            {isMobile && displayData?.impact && (
              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-start' }}>
                <Box
                  sx={{
                    background: '#1A55F9',
                    borderRadius: '999px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    px: 2,
                    height: 32,
                  }}
                >
                  <Typography
                    sx={{
                      color: '#ffffff',
                      fontFamily: 'Poppins',
                      fontWeight: 600,
                      fontSize: '12px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.02em',
                    }}
                  >
                    {String(displayData?.impact)}
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>

          {/* Main content grid */}
          <Grid container spacing={2} sx={{ p: 2 }}>
            <Grid size={{ xs: 12, md: 12 }} spacing={2}>
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                {/* Tab bar */}
                <CardContent
                  sx={{
                    border: '1px solid #DDE1E6',
                    boxShadow: 'none',
                    borderRadius: '10px',
                    mb: 2,
                    p: 0,
                  }}
                >
                  <Box
                    sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                  >
                    {[
                      { key: 'information', label: 'Key Information' },
                      { key: 'agents', label: showAiAutomation ? 'AI Automation' : 'Agents' },
                    ].map((tab, index) => (
                      <Box
                        key={tab.key}
                        sx={{
                          flex: 1,
                          height: '43px',
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          cursor: 'pointer',
                          borderLeft: index === 1 ? '1px solid #DDE1E6' : 'none',
                        }}
                        onClick={() => handleTabClick(tab.key)}
                      >
                        <Typography
                          variant="h6"
                          sx={{
                            color: activePiece === tab.key ? '#1A55F9' : '#000000',
                            borderBottom: activePiece === tab.key ? '2px solid #1A55F9' : 'none',
                            pb: activePiece !== tab.key ? '2px' : 0,
                            fontSize: '16px',
                            fontWeight: 600,
                          }}
                        >
                          {tab.label}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </CardContent>

                {/* ── TAB: Key Information ───────────────────────────────── */}
                {activePiece === 'information' ? (
                  <Grid
                    container
                    spacing={2}
                    sx={{
                      height: isMobile ? 'auto' : 'calc(100vh - 280px)',
                      minHeight: isMobile ? 0 : 'auto',
                    }}
                  >
                    <Grid
                      container
                      spacing={2}
                      sx={{
                        height: '100%',
                        overflowY: 'auto',
                        scrollbarWidth: 'none',
                        '&::-webkit-scrollbar': { display: 'none' },
                      }}
                    >
                      {/* Left: overview + data panels */}
                      <Grid size={{ xs: 12, md: 9 }}>
                        <Card
                          sx={{
                            mb: 2,
                            boxShadow: 'none',
                            border: !isProcessAiOpportunities ? '1px solid #E0E0E0' : 'none',
                          }}
                        >
                          <CardContent sx={{ padding: isProcessAiOpportunities ? 0 : 2 }}>
                            {/* Overview */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                              <Typography sx={{ fontWeight: 600, fontSize: '18px' }}>
                                Overview
                              </Typography>
                              {isEditing && (
                                <DriveFileRenameOutlineIcon
                                  sx={{ cursor: 'pointer', fontSize: 18, color: 'grey.600' }}
                                  onClick={() => handleEdit('overview')}
                                />
                              )}
                            </Box>
                            <Typography sx={{ fontSize: '14px', lineHeight: 1.6 }}>
                              {displayData?.description}
                            </Typography>

                            {/* Performance Improvement */}
                            {displayData?.performance_improvement && (
                              <Box sx={{ mt: 3 }}>
                                <Typography sx={{ fontWeight: 600, mb: 1, fontSize: '18px' }}>
                                  Performance Improvement Detail
                                </Typography>
                                <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>
                                  Supports Performance Improvement:
                                </Typography>
                                <Stack spacing={1} mt={1}>
                                  {(
                                    displayData.performance_improvement
                                      .supports_performance_improvement || []
                                  ).map((item: string, i: number) => (
                                    <Typography
                                      key={i}
                                      sx={{
                                        bgcolor: '#F3F6FF',
                                        minHeight: '36px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        px: 1.5,
                                        py: 0.5,
                                        borderRadius: '4px',
                                        fontWeight: 500,
                                        fontSize: '14px',
                                      }}
                                    >
                                      {item}
                                    </Typography>
                                  ))}
                                </Stack>
                              </Box>
                            )}

                            {displayData?.performance_improvement?.value_driver && (
                              <Box sx={{ mt: 3 }}>
                                <Typography sx={{ fontWeight: 600, mb: 1, fontSize: '14px' }}>
                                  Value Driver
                                </Typography>
                                <Typography sx={{ fontSize: '14px', lineHeight: 1.6 }}>
                                  {displayData.performance_improvement.value_driver}
                                </Typography>
                              </Box>
                            )}

                            <Divider sx={{ my: 1 }} />

                            {/* ROI + Complexity */}
                            <Grid container alignItems="stretch">
                              <Grid size={{ xs: 6, md: 5.9 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Typography sx={{ fontSize: '14px', color: '#666' }}>
                                    ROI
                                  </Typography>
                                  {isEditing && (
                                    <DriveFileRenameOutlineIcon
                                      sx={{ cursor: 'pointer', fontSize: 16 }}
                                      onClick={() => handleEdit('roi')}
                                    />
                                  )}
                                </Box>
                                <Typography
                                  sx={{ fontWeight: 600, display: 'flex', alignItems: 'center' }}
                                >
                                  <img
                                    src={getSvgPathForRoi(displayData?.roi)}
                                    alt=""
                                    style={{ width: 10, height: 10, marginRight: 3 }}
                                  />
                                  {displayData?.roi}
                                </Typography>
                                <Typography sx={{ fontSize: '13px' }}>
                                  {displayData?.roi_description}
                                </Typography>
                              </Grid>
                              <Grid
                                size={{ xs: 0, md: 0.2 }}
                                sx={{
                                  display: { xs: 'none', md: 'flex' },
                                  justifyContent: 'center',
                                  pr: 1,
                                }}
                              >
                                <Divider orientation="vertical" flexItem />
                              </Grid>
                              <Grid size={{ xs: 6, md: 5.9 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Typography sx={{ fontSize: '14px', color: '#666' }}>
                                    Complexity
                                  </Typography>
                                  {isEditing && (
                                    <DriveFileRenameOutlineIcon
                                      sx={{ cursor: 'pointer', fontSize: 16 }}
                                      onClick={() => handleEdit('complexity')}
                                    />
                                  )}
                                </Box>
                                <Typography
                                  sx={{ fontWeight: 600, display: 'flex', alignItems: 'center' }}
                                >
                                  <img
                                    src={getSvgPathForComplexity(displayData?.complexity)}
                                    alt=""
                                    style={{ width: 10, height: 10, marginRight: 3 }}
                                  />
                                  {displayData?.complexity}
                                </Typography>
                                <Typography sx={{ fontSize: '13px' }}>
                                  {displayData?.complexity_description}
                                </Typography>
                              </Grid>
                            </Grid>
                          </CardContent>
                        </Card>

                        {/* Data Sources + Key Requirements panels */}
                        <Grid
                          container
                          spacing={2}
                          sx={{ flexDirection: { xs: 'column', md: 'row' } }}
                        >
                          <Grid size={{ xs: 12, md: 6 }}>
                            <DataSourcesPanel
                              displayData={displayData}
                              llmResponseData={llmResponseData}
                              llmResponseLoading={llmResponseLoading}
                              isLoadingImplementation={isLoadingImplementation}
                              isEditing={isEditing}
                              onEdit={handleEdit}
                            />
                          </Grid>
                          <Grid size={{ xs: 12, md: 6 }}>
                            <KeyRequirementsPanel
                              keyRequirements={displayData?.key_requirements || []}
                              isEditing={isEditing}
                              isLoadingImplementation={isLoadingImplementation}
                              onEdit={handleEdit}
                            />
                          </Grid>
                        </Grid>
                      </Grid>

                      {/* Right sidebar: benefits + implementation complexity */}
                      <Grid size={{ xs: 12, md: 3 }}>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            flexDirection: 'column',
                            gap: 2,
                          }}
                        >
                          <TopBenefits
                            allTopBenefits={
                              displayData?.benefits
                                ? Object.entries(displayData.benefits).map(([key, value]) => ({
                                    title: getBenefitTitle(key),
                                    label: getBenefitLabelNew(value as string),
                                  }))
                                : []
                            }
                            isEditing={isEditing}
                            solutionData={updatedSolutionData}
                            setSolutionData={setUpdatedSolutionData}
                          />
                          {(() => {
                            const ic = displayData?.implementation_complexity;
                            const hasValidComplexity =
                              ic &&
                              ['integrations', 'data_sources', 'automation', 'governance'].some(
                                (k) => ic[k]
                              );
                            if (!hasValidComplexity) return null;
                            return (
                              <ImplementationComplexity
                                key={`impl-complexity-${selectedSolutionId}`}
                                complexityData={ic}
                                isEditing={isEditing}
                                solutionData={updatedSolutionData}
                                setSolutionData={setUpdatedSolutionData}
                              />
                            );
                          })()}
                        </Box>
                      </Grid>
                    </Grid>
                  </Grid>
                ) : /* ── TAB: Agents / AI Automation ────────────────────────── */
                !hasEnterpriseTechApplied ? (
                  <Box
                    sx={{
                      width: '100%',
                      minHeight: '220px',
                      bgcolor: '#F6F8FC',
                      borderRadius: '8px',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      textAlign: 'center',
                      gap: 1.5,
                      px: 2,
                    }}
                  >
                    <Typography sx={{ fontSize: '18px', fontWeight: 600, color: '#000' }}>
                      Enablement Not Available
                    </Typography>
                    <Typography sx={{ fontSize: '14px', color: '#5F6368', lineHeight: 1.6 }}>
                      Configure your technology landscape to see the enablement available for this
                      opportunity.
                    </Typography>
                    <Button
                      variant="contained"
                      sx={{
                        mt: 1,
                        backgroundColor: '#1A55F9',
                        textTransform: 'none',
                        borderRadius: '6px',
                        px: 3,
                        py: 1,
                        fontSize: '14px',
                        fontWeight: 500,
                        '&:hover': { backgroundColor: '#1746d1' },
                      }}
                      onClick={() => navigate('/enterprise-context')}
                    >
                      Configure Technology Landscape
                    </Button>
                  </Box>
                ) : (
                  <Box sx={{ overflow: 'auto', height: isMobile ? 'auto' : 'calc(100vh - 280px)' }}>
                    {showAiAutomation ? (
                      <AiEnablementView
                        normalizedAgentData={normalizedAgentData}
                        isLoading={isIdeationEnablementLoading}
                        isEditing={isEditing}
                        onEdit={handleEdit}
                      />
                    ) : (
                      <AgentsView
                        llmAgents={displayData?.llm_agent || []}
                        agentEmbeddedArray={displayData?.agent?.embedded || []}
                        agentLlmResponse={agentLlmResponse}
                        isLoadingAgentLlmResponse={isLoadingAgentLlmResponse}
                        isEditing={isEditing}
                        onEdit={handleEdit}
                      />
                    )}
                  </Box>
                )}
              </Box>
            </Grid>

            {/* ── Modals ─────────────────────────────────────────────────── */}
            <ConfirmationModal
              isOpen={showDeleteConfirmModal}
              title="Delete Opportunity"
              description={
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography sx={{ color: '#4f4f4f' }}>
                    Are you sure you want to delete this opportunity? This action cannot be undone.
                    All associated data will be permanently removed.
                  </Typography>
                </Box>
              }
              primaryButtonText={
                <Box display="flex" alignItems="center" gap={1}>
                  Delete
                </Box>
              }
              onPrimaryAction={() => {
                handleRemoveClick?.();
                setShowDeleteConfirmModal(false);
              }}
              onClose={() => setShowDeleteConfirmModal(false)}
              width="781px"
              outerPadding={0}
              sx={effectiveIsIdeation ? { zIndex: 1500 } : undefined}
            />

            <Modal
              isOpen={showFavoriteModal}
              onClose={() => setShowFavoriteModal(false)}
              title={
                isFavorite ? 'Remove AI Solution from Favorites' : 'Add AI Solution to Favorites'
              }
              maxWidth="sm"
              height="md"
            >
              <Box sx={{ bgcolor: '#F9FBFF', borderRadius: '12px', p: 3 }}>
                <Stack spacing={2} alignItems="center">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {isFavorite ? (
                      <Star sx={{ color: '#2054F4' }} />
                    ) : (
                      <StarBorder sx={{ color: '#2054F4' }} />
                    )}
                    <Typography sx={{ fontSize: '16px', color: '#2c2c2c' }}>
                      Are you sure you want to{' '}
                      {isFavorite ? 'remove this AI Solution from' : 'add this AI Solution to'} your
                      favorites?
                    </Typography>
                  </Box>
                  <Button
                    variant="contained"
                    onClick={handleToggleFavorite}
                    sx={{
                      textTransform: 'none',
                      bgcolor: '#2054F4',
                      borderRadius: '6px',
                      px: 3,
                      py: 1,
                      '&:hover': { bgcolor: '#1A45C6' },
                      fontWeight: 'bold',
                    }}
                    startIcon={<Add />}
                  >
                    {isFavorite ? 'Remove' : 'Add'}
                  </Button>
                </Stack>
              </Box>
            </Modal>

            <Snackbar
              open={showApplySuccessSnackbar}
              autoHideDuration={3000}
              onClose={() => setShowApplySuccessSnackbar(false)}
              anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
              <Alert
                onClose={() => setShowApplySuccessSnackbar(false)}
                severity="success"
                sx={{ width: '100%' }}
              >
                AI Solution applied successfully
              </Alert>
            </Snackbar>
          </Grid>

          {openEditModal && (
            <SolutionEditModal
              isOpen={openEditModal}
              solutionData={updatedSolutionData}
              setSolutionData={setUpdatedSolutionData}
              onSave={handleSaveSolution}
              onClose={() => setOpenEditModal(false)}
              type={editType as EditType}
              editItem={editItem || undefined}
              setLlmResponseData={setLlmResponseData}
            />
          )}
        </Box>
      </Box>
    </>
  );
};

export default ViewSolutionDetails;
